import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  ButtonGroup,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Grid
} from '@mui/material';
import {
  Upload as UploadIcon,
  Download as DownloadIcon,
  GetApp as GetAppIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon
} from '@mui/icons-material';

const BulkCertificates = () => {
  const [mode, setMode] = useState('generation'); // 'generation' or 'download'
  const [batchId, setBatchId] = useState('');
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      if (selectedFile.name.endsWith('.csv')) {
        setFile(selectedFile);
        setFileName(selectedFile.name);
        setError('');
      } else {
        setError('Please select a CSV file');
        setFile(null);
        setFileName('');
      }
    }
  };

  const handleSubmit = async () => {
    if (!batchId.trim()) {
      setError('Please enter a batch ID');
      return;
    }

    if (!file) {
      setError('Please select a CSV file');
      return;
    }

    setLoading(true);
    setError('');
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('batch_id', batchId.trim());
      formData.append('format', 'zip'); // Request ZIP format

      // Choose endpoint based on mode
      const endpoint = mode === 'generation' 
        ? 'https://sims.ethnotech.in/api/certificates/upload-csv'
        : 'https://sims.ethnotech.in/api/certificates/download-csv';

      const token = localStorage.getItem('authToken');
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/zip')) {
          // Handle ZIP file download
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `certificates_batch_${batchId}.zip`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          setUploadProgress(100);
        } else {
          // Handle JSON response
          const data = await response.json();
          setResults(data);
          setUploadProgress(100);
        }
      } else {
        const data = await response.json();
        setError(data.detail || `Certificate ${mode} failed`);
      }
    } catch (err) {
      setError(`Network error: ${err.message}`);
      console.error(`Certificate ${mode} error:`, err);
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem('authToken');
      
      // Choose template endpoint based on mode
      const endpoint = mode === 'generation' 
        ? 'https://sims.ethnotech.in/api/certificates/generation-template'
        : 'https://sims.ethnotech.in/api/certificates/csv-template';
      
      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = mode === 'generation' 
          ? 'certificate_generation_template.csv'
          : 'certificate_download_template.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        setError('Failed to download template');
      }
    } catch (err) {
      setError(`Error downloading template: ${err.message}`);
    }
  };

  const downloadResults = () => {
    if (!results || !results.results) return;

    const csvContent = [
      ['Candidate ID', 'Status', 'Message', 'Certificate URL'].join(','),
      ...results.results.map(result => [
        result.candidateId || '',
        result.success ? 'Success' : 'Failed',
        result.message || '',
        result.certificateUrl || ''
      ].map(field => `"${field}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `certificate_downloads_batch_${batchId}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const getStatusChip = (success) => {
    return success ? (
      <Chip
        icon={<CheckCircleIcon />}
        label="Success"
        color="success"
        size="small"
      />
    ) : (
      <Chip
        icon={<ErrorIcon />}
        label="Failed"
        color="error"
        size="small"
      />
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ 
        background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        fontWeight: 'bold',
        mb: 3
      }}>
        Bulk Certificate Management
      </Typography>

      {/* Mode Selection Tabs */}
      <Box sx={{ mb: 3 }}>
        <ButtonGroup variant="outlined" sx={{ mb: 2 }}>
          <Button
            variant={mode === 'generation' ? 'contained' : 'outlined'}
            onClick={() => {
              setMode('generation');
              setResults(null);
              setError('');
              setFile(null);
              setFileName('');
            }}
            startIcon={<CloudUploadIcon />}
          >
            Certificate Generation
          </Button>
          <Button
            variant={mode === 'download' ? 'contained' : 'outlined'}
            onClick={() => {
              setMode('download');
              setResults(null);
              setError('');
              setFile(null);
              setFileName('');
            }}
            startIcon={<GetAppIcon />}
          >
            Certificate Download
          </Button>
        </ButtonGroup>
        
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          {mode === 'generation' 
            ? 'Generate new certificates for candidates by uploading a CSV file with candidate usernames' 
            : 'Download existing certificates for candidates by uploading a CSV file with candidate IDs'}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Input Section */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            boxShadow: 3 
          }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ 
                color: '#1976d2',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <InfoIcon />
                Certificate {mode === 'generation' ? 'Generation' : 'Download'} Setup
              </Typography>

              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Batch ID"
                  value={batchId}
                  onChange={(e) => setBatchId(e.target.value)}
                  placeholder={mode === 'generation' 
                    ? "Enter the batch ID for certificate generation" 
                    : "Enter the batch ID for certificate download"}
                  disabled={loading}
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
                

                <input
                  key={mode}
                  accept=".csv"
                  style={{ display: 'none' }}
                  id="csv-file-input"
                  type="file"
                  onChange={handleFileChange}
                  disabled={loading}
                />
                <label htmlFor="csv-file-input">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadIcon />}
                    disabled={loading}
                    sx={{ mb: 2 }}
                  >
                    Select CSV File
                  </Button>
                </label>

                {fileName && (
                  <Typography variant="body2" sx={{ color: 'green', mb: 2 }}>
                    Selected: {fileName}
                  </Typography>
                )}
              </Box>

              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading || !file || !batchId.trim()}
                startIcon={mode === 'generation' ? <CloudUploadIcon /> : <GetAppIcon />}
                sx={{
                  background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
                  color: 'white',
                  fontWeight: 'bold',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #1565c0, #1976d2)',
                  }
                }}
                fullWidth
              >
                {loading 
                  ? (mode === 'generation' ? 'Generating Certificates...' : 'Downloading Certificates...')
                  : (mode === 'generation' ? 'Generate Certificates' : 'Download Certificates')
                }
              </Button>

              {loading && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress 
                    variant="indeterminate" 
                    sx={{ 
                      height: 8, 
                      borderRadius: 5,
                      background: 'rgba(25, 118, 210, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
                      }
                    }} 
                  />
                  <Typography variant="body2" sx={{ textAlign: 'center', mt: 1, color: '#666' }}>
                    Processing certificate {mode}...
                  </Typography>
                </Box>
              )}

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Instructions Section */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            boxShadow: 3 
          }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Instructions
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                Follow these steps for bulk certificate generation:
              </Typography>
              <Box component="ol" sx={{ pl: 2, '& li': { mb: 1, opacity: 0.9 } }}>
                <li>Enter a valid Batch ID</li>
                <li>Use your existing candidate registration CSV file (with "User name" column)</li>
                <li>Or download the template to see the expected format</li>
                <li>Upload the CSV file containing candidate IDs</li>
                <li>Click "Generate Certificates" to process</li>
                <li>Download results to see certificate URLs and status</li>
              </Box>
              <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic', opacity: 0.8 }}>
                You can reuse the same CSV file from candidate registration - no need to create a new one!
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Results Section */}
      {results && (
        <Card sx={{ mt: 3, boxShadow: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                Certificate {mode === 'generation' ? 'Generation' : 'Download'} Results
              </Typography>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={downloadResults}
                size="small"
              >
                Download Results
              </Button>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Chip 
                label={`Total: ${results.total || 0}`} 
                color="default" 
                variant="outlined" 
              />
              <Chip 
                label={`Successful: ${results.successful || 0}`} 
                color="success" 
                variant="outlined" 
              />
              <Chip 
                label={`Failed: ${results.failed || 0}`} 
                color="error" 
                variant="outlined" 
              />
            </Box>

            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Candidate ID</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Message</strong></TableCell>
                    <TableCell><strong>{mode === 'generation' ? 'Certificate URL' : 'Download URL'}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.results?.map((result, index) => (
                    <TableRow key={index}>
                      <TableCell>{result.candidateId}</TableCell>
                      <TableCell>{getStatusChip(result.success)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ 
                          maxWidth: 300, 
                          wordBreak: 'break-word',
                          color: result.success ? '#2e7d32' : '#d32f2f'
                        }}>
                          {result.message}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {mode === 'generation' ? (
                          result.certificateUrl ? (
                            <Tooltip title="Download Certificate">
                              <IconButton
                                size="small"
                                onClick={() => window.open(result.certificateUrl, '_blank')}
                                color="primary"
                              >
                                <DownloadIcon />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="textSecondary">-</Typography>
                          )
                        ) : (
                          result.downloadUrl ? (
                            <Tooltip title="Download Certificate">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const token = localStorage.getItem('authToken');
                                  // Create a temporary link element
                                  const link = document.createElement('a');
                                  link.href = result.downloadUrl;
                                  // Add authorization header through URL parameters
                                  if (token) {
                                    link.href += `&token=${token}`;
                                  }
                                  link.target = '_blank';
                                  link.rel = 'noopener noreferrer';
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                }}
                                color="primary"
                              >
                                <DownloadIcon />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="textSecondary">-</Typography>
                          )
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default BulkCertificates;
